import { ComponentType } from "react";

// Inspection type values for context cards
export type InspectionType = "site_safety" | "workmanship";

// Advisor type values for field advisor cards
export type AdvisorType = "analyze" | "execute" | "review";

// Setting scope type values
export type SettingScope = "personal" | "board";

// Card type values for context cards
export type CardType =
  | "role"
  | "site_context"
  | "focus_and_exclusions"
  | "conditional_instructions"
  | "risk_and_tolerance"
  | "insight_intent"
  | "constraints"
  | "output_content"
  | "quality_check"
  | "trade_appendices";

// Field advisor card type values
export type FieldAdvisorCardType =
  | "persona"
  | "context"
  | "core_goal"
  | "decision_priorities"
  | "special_skills"
  | "use_of_documents"
  | "tone_and_teaching"
  | "output_content"
  | "questions_to_answer"
  | "limits";

// Promote type for promote prompt cards
export type PromoteType = "promote";

// Promote card type values
export type PromoteCardType =
  | "role"
  | "domain_context"
  | "declared_intents"
  | "tagging_philosophy"
  | "folder_suggestion_logic"
  | "temporal_classification"
  | "tagging_rules_and_restraint"
  | "restriction_and_visibility_rules"
  | "editorial_output_rules";

// Import detail components
import TeamDetail from "../detailComponents/TeamDetail";
import IconDetail from "../detailComponents/IconDetail";
import CompanyCamIntegrationDetail from "../detailComponents/CompanyCamIntegrationDetail";
import PermissionDetail from "../detailComponents/PermissionDetail";
import AIPromptSelector from "../detailComponents/AIPromptSelector";
import NotificationDetail from "../detailComponents/NotificationDetail";
import ScheduleDetail from "../detailComponents/ScheduleDetail";
import ShareScheduleDetail from "../detailComponents/ShareScheduleDetail";
import BoardSettingsDetail from "../detailComponents/BoardSettingsDetail";
import OpenAIDetail from "../detailComponents/OpenAIDetail";
import GeneralBoardSettingDetail from "../detailComponents/GeneralBoardSettingDetail";
import SwivuAssistDetail from "../detailComponents/SwivuAssistDetail";
import BoardImportExportDetails from "../detailComponents/BoardImportExportDetails";
import GeneralTaskSettingsDetail from "../detailComponents/GeneralTaskSettingsDetail";
import TaskAutoArchiveDetail from "../detailComponents/TaskAutoArchiveDetail";
import BudgetSettingsDetail from "../detailComponents/BudgetSettingsDetail";
import ClientCategoryDetail from "../detailComponents/ClientCategoryDetail";
import ClientTagDetail from "../detailComponents/ClientTagDetail";
import ClientStatusDetail from "../detailComponents/ClientStatusDetail";
import ContactCategoryDetail from "../detailComponents/ContactCategoryDetail";
import ContactTagDetail from "../detailComponents/ContactTagDetail";
import ContactStatusDetail from "../detailComponents/ContactStatusDetail";
import ResourceCategoryDetail from "../detailComponents/ResourceCategoryDetail";
import ResourceTagDetail from "../detailComponents/ResourceTagDetail";
import ResourceStatusDetail from "../detailComponents/ResourceStatusDetail";
import TaskCategoryDetail from "../detailComponents/TaskCategoryDetail";
import TaskTagDetail from "../detailComponents/TaskTagDetail";
import TaskStatusDetail from "../detailComponents/TaskStatusDetail";
import TaskPhaseDetail from "../detailComponents/TaskPhaseDetail";
import ResourceTypeDetail from "../detailComponents/ResourceTypeDetail";
import IncentiveCategoriesAndTagsDetail from "../detailComponents/IncentiveCategoriesAndTagsDetail";
import IncentivePermissionDetail from "../detailComponents/IncentivePermissionDetail";
import { PersonalScheduleDetail } from "../detailComponents/PersonalScheduleDetail";
import NonWorkingTimeDetail from "../detailComponents/NonWorkingTimeDetail";
import SMMGeneratorDetail from "../detailComponents/smmGenerator/SMMGeneratorDetail";
import BoardContextDetail from "../detailComponents/boardContext/BoardContextDetail";
export interface SubcategoryDetailProps {
  subcategory: Subcategory;
  categoryId?: string;
}

export interface Subcategory {
  id: string;
  label: string;
  description: string;
  component: ComponentType<SubcategoryDetailProps>;
  settingScope?: SettingScope; // Scope of the setting: "personal" or "board"
  settingId?: string; // Backend setting id; used to deduplicate search results when same setting appears with different indexed ids
  required?: boolean; // Show "Required" badge, enforce min 1 card
  inspectionType?: InspectionType; // For context cards: "site_safety" or "workmanship"
  cardType?: CardType; // For context cards: the backend card_type value
  advisorType?: AdvisorType; // For field advisor cards: "analyze", "execute", or "review"
  fieldAdvisorCardType?: FieldAdvisorCardType; // For field advisor cards: the backend card_type value
  promoteType?: PromoteType; // For promote prompt cards: "promote"
  promoteCardType?: PromoteCardType; // For promote prompt cards: the backend card_type value
  terms?: string[]; // Additional search terms for improved search functionality
}

export interface SettingsCategory {
  id: string;
  label: string;
  icon: string;
  subcategories: Subcategory[];
}

export const SETTING_KEYS = {
  // Personal settings
  NOTIFICATIONS: "set-notifications",
  PERSONAL_SCHEDULE: "set-personal-schedule",
  PERSONAL_BOARD: "set-personal-board",
  ACCESS_PERMISSIONS: "set-access-permissions",

  // General settings
  GENERAL_SETTINGS: "set-general-settings",
  SWIVU_ASSIST: "set-swivu-assist",
  IMPORT_EXPORT: "set-import-export",
  ICONS: "set-icons",
  BUDGET_SETTINGS: "set-budget-settings",

  // Schedule settings
  SHARE_SCHEDULE: "set-share-schedule",
  PERSONAL_SCHEDULE_VIEW_SETTINGS: "set-personal-schedule-view-settings",
  NON_WORKING_TIME: "set-non-working-time",

  // Client settings
  CLIENT_CATEGORIES: "set-client-categories",
  CLIENT_TAGS: "set-client-tags",
  CLIENT_STATUSES: "set-client-statuses",

  // Contact settings
  CONTACT_TAG: "set-contact-tags",
  CONTACT_CATEGORIES: "set-contact-categories",
  CONTACT_TAGS: "set-contact-tags",
  CONTACT_STATUSES: "set-contact-statuses",

  // Incentive settings
  INCENTIVE_PERMISSIONS: "set-incentive-permissions",
  INCENTIVE_CATEGORIES_AND_TAGS: "set-incentive-categories-and-tags",

  // Integration settings
  INTEGRATION_OPENAI: "set-openai",
  INTEGRATION_COMPANY_CAM: "set-integerations",

  // Resource settings
  RESOURCE_TYPES: "set-resource-types",
  RESOURCE_CATEGORIES: "set-resource-categories",
  RESOURCE_TAGS: "set-resource-tags",
  RESOURCE_STATUSES: "set-resource-statuses",

  // Task settings
  TASK_GENERAL: "set-task-general",
  TASK_AUTO_ARCHIVE: "set-task-auto-archive",
  TASK_CATEGORIES: "set-task-categories",
  TASK_TAGS: "set-task-tags",
  TASK_STATUSES: "set-task-statuses",
  TASK_PHASE: "set-task-phase",

  // Team settings
  TEAMS_GENERAL: "set-teams-general",

  // AI Prompt settings
  SITE_SAFETY_PROMPT: "set-site-safety-prompt",
  WORKMANSHIP_PROMPT: "set-workmanship-prompt",
  ANALYZE_PROMPT: "set-analyze-prompt",
  EXECUTE_PROMPT: "set-execute-prompt",
  REVIEW_PROMPT: "set-review-prompt",

  // Promote Prompt settings
  PROMOTE_PROMPT: "set-promote-prompt",

  // SMM Generator
  SMM_GENERATOR: "set-smm-generator",

  // Board Context
  BOARD_CONTEXT: "set-board-context",
};

export const SETTING_COMPONENT_MAP = {
  // Personal settings
  [SETTING_KEYS.NOTIFICATIONS]: NotificationDetail,
  [SETTING_KEYS.PERSONAL_SCHEDULE]: ScheduleDetail,
  [SETTING_KEYS.PERSONAL_BOARD]: BoardSettingsDetail,
  [SETTING_KEYS.ACCESS_PERMISSIONS]: PermissionDetail,

  // General settings
  [SETTING_KEYS.GENERAL_SETTINGS]: GeneralBoardSettingDetail,
  [SETTING_KEYS.SWIVU_ASSIST]: SwivuAssistDetail,
  [SETTING_KEYS.IMPORT_EXPORT]: BoardImportExportDetails,
  [SETTING_KEYS.ICONS]: IconDetail,
  [SETTING_KEYS.BUDGET_SETTINGS]: BudgetSettingsDetail,

  // Schedule settings
  [SETTING_KEYS.SHARE_SCHEDULE]: ShareScheduleDetail,
  [SETTING_KEYS.PERSONAL_SCHEDULE_VIEW_SETTINGS]: PersonalScheduleDetail,
  [SETTING_KEYS.NON_WORKING_TIME]: NonWorkingTimeDetail,

  // Client settings
  [SETTING_KEYS.CLIENT_CATEGORIES]: ClientCategoryDetail,
  [SETTING_KEYS.CLIENT_TAGS]: ClientTagDetail,
  [SETTING_KEYS.CLIENT_STATUSES]: ClientStatusDetail,

  // Contact settings
  [SETTING_KEYS.CONTACT_CATEGORIES]: ContactCategoryDetail,
  [SETTING_KEYS.CONTACT_TAGS]: ContactTagDetail,
  [SETTING_KEYS.CONTACT_STATUSES]: ContactStatusDetail,

  // Incentive settings
  [SETTING_KEYS.INCENTIVE_PERMISSIONS]: IncentivePermissionDetail,
  [SETTING_KEYS.INCENTIVE_CATEGORIES_AND_TAGS]: IncentiveCategoriesAndTagsDetail,

  // Integration settings
  [SETTING_KEYS.INTEGRATION_OPENAI]: OpenAIDetail,
  [SETTING_KEYS.INTEGRATION_COMPANY_CAM]: CompanyCamIntegrationDetail,

  // Resource settings
  [SETTING_KEYS.RESOURCE_TYPES]: ResourceTypeDetail,
  [SETTING_KEYS.RESOURCE_CATEGORIES]: ResourceCategoryDetail,
  [SETTING_KEYS.RESOURCE_TAGS]: ResourceTagDetail,
  [SETTING_KEYS.RESOURCE_STATUSES]: ResourceStatusDetail,

  // Task settings
  [SETTING_KEYS.TASK_GENERAL]: GeneralTaskSettingsDetail,
  [SETTING_KEYS.TASK_AUTO_ARCHIVE]: TaskAutoArchiveDetail,
  [SETTING_KEYS.TASK_CATEGORIES]: TaskCategoryDetail,
  [SETTING_KEYS.TASK_TAGS]: TaskTagDetail,
  [SETTING_KEYS.TASK_STATUSES]: TaskStatusDetail,
  [SETTING_KEYS.TASK_PHASE]: TaskPhaseDetail,

  // Team settings
  [SETTING_KEYS.TEAMS_GENERAL]: TeamDetail,

  // AI Prompt settings
  [SETTING_KEYS.SITE_SAFETY_PROMPT]: AIPromptSelector,
  [SETTING_KEYS.WORKMANSHIP_PROMPT]: AIPromptSelector,
  [SETTING_KEYS.ANALYZE_PROMPT]: AIPromptSelector,
  [SETTING_KEYS.EXECUTE_PROMPT]: AIPromptSelector,
  [SETTING_KEYS.REVIEW_PROMPT]: AIPromptSelector,

  // Promote Prompt settings
  [SETTING_KEYS.PROMOTE_PROMPT]: AIPromptSelector,

  // SMM Generator
  [SETTING_KEYS.SMM_GENERATOR]: SMMGeneratorDetail,

  // Board Context
  [SETTING_KEYS.BOARD_CONTEXT]: BoardContextDetail,
};

/**
 * AI-specific configuration for settings that use AIPromptSelector component
 * Maps settingId to AI-specific metadata (inspectionType or advisorType)
 */
export const AI_SETTING_CONFIG: Record<string, Partial<Subcategory>> = {
  // Inspection Prompts
  [SETTING_KEYS.SITE_SAFETY_PROMPT]: { inspectionType: "site_safety" },
  [SETTING_KEYS.WORKMANSHIP_PROMPT]: { inspectionType: "workmanship" },

  // Field Advisor Prompts
  [SETTING_KEYS.ANALYZE_PROMPT]: { advisorType: "analyze" },
  [SETTING_KEYS.EXECUTE_PROMPT]: { advisorType: "execute" },
  [SETTING_KEYS.REVIEW_PROMPT]: { advisorType: "review" },

  // Promote Prompt
  [SETTING_KEYS.PROMOTE_PROMPT]: { promoteType: "promote" }
};

// CSV Data Types (imported from useSettings hook)
export interface SettingsRow {
  IntentAreaId: string;
  IntentAreaName: string;
  IntentAreaIcon: string;
  settingId: string;
  settingName: string;
  settingSubtitle: string;
  settingScope: string;
  settingKeywords: string;
}

export interface SettingsData {
  [intentAreaId: string]: SettingsRow[];
}
